import { Alert, Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/client";
import toast from "react-hot-toast";

import { addCompanyPolicy } from "../../graphql/company-policy/addCompanyPolicy";
import { removeCompanyPolicy } from "../../graphql/company-policy/removeCompanyPolicy";
import { updateCompanyPolicy } from "../../graphql/company-policy/updateCompanyPolicy";
import { useGetPolicyList } from "../../hooks/company-policy/useCompanyPolicies";
import { useGetActivePolicyTags } from "../../hooks/company-policy/useCompanyPolicyTags";

import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { FileToText } from "../../helper/Utilities";
import { CompanyPolicyStatusOptions, CompanyPolicyStatus, ValidFileFormatForPolicy, SelectOption, COMPANY_POLICY_UNCATEGORIZED_TAG } from "../../helper/constants";
import PromptDialog from "../../components/PromptDialog";
import { Field, Form, Formik, FormikProps } from "formik";
import TextFormInput from "../../components/Shared/Forms/TextFormInput";
import { CompanyPolicy } from "../../interfaces/models/CompanyPolicy";
import TextCounter from "../../components/Shared/Forms/TextCounter";
import { AddCompayPolicyValidator, MAX_TITLE_LENGTH }  from "../../validators/AddCompanyPolicyValidator";
import FormError from "../../components/Shared/Forms/FormError";
import CompanyPolicyAndNudgeHistoryModal from "../../components/Compliance/CompanyPolicyAndNudgeHistoryModal";
import RichFormSelectCreatable from "../../components/Shared/Forms/RichFormSelectCreatable";
import RichFormSelect from "../../components/Shared/Forms/RichFormSelect";
import { CompanyPolicyTag } from "../../interfaces/models/CompanyPolicyTag";
import CompanyPolicyTable from "../../components/Compliance/CompanyPolicyTable";

interface FormData {
  name: string;
  status: number;
  policy_meta_data?: string;
  policy_file_name?: string;
  tags: SelectOption[];
  file?: File;
}

interface ModalParams {
  name: string;
  action: string;
}

interface FilterParams {
  search: string,
  status: SelectOption;
  tag: SelectOption
}

const CompanyPolicyListPage = () => {
  const history = useHistory();

  const breadcrumbsData = [
    { name: "Complaince", path: `/compliance` },
    { name: "Policy Hub", path: `/compliance/policy-hub` },
  ];

  const StatusFilterOptions: SelectOption[] = [
    { label: 'Show Active Policies', value: CompanyPolicyStatus.ACTIVE.toString() },
    { label: 'Show Archived Policies', value: CompanyPolicyStatus.ARCHIVED.toString() }
  ]

  const [policies, setPolicies] = useState<CompanyPolicy[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [filter, setFilter] = useState<FilterParams>({
    search: "",
    status: StatusFilterOptions[0],
    tag: null
  });

  const [modal, setModal] = useState<ModalParams>({
    name: "Add",
    action: "add",
  });
  const [isAddPolicyModalOpen, setIsAddPolicyModalOpen] = useState<boolean>(false);
  const [selectedPolicy, setselectedPolicy] = useState<CompanyPolicy>(null);
  const [isOnProcess, setIsOnProcess] = useState<boolean>(false);
  const formikRef = useRef<FormikProps<FormData>>(null);
  const [initialValuePolicy, setInitialValuePolicy] = useState<FormData>({
    name: "",
    status: CompanyPolicyStatus.ACTIVE,
    tags: []
  });

  const [openDeletePolicyModal, setOpenDeletePolicyModal] = useState<boolean>(false);
  const [isDeletingPolicy, setIsDeletingPolicy] = useState<boolean>(false);
  
  const [selectedPolicyHistory, setSelectedPolicyHistory] = useState<CompanyPolicy>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState<boolean>(false);

  const [tags, setTags] = useState<SelectOption[]>([]);
  const [selectedTags, setSelectedTags] = useState<SelectOption[]>([]);

  const { data, loading, error, refetch } = useGetPolicyList();
  const tagResult = useGetActivePolicyTags();
  
  const [addCompanyPolicyHook] = useMutation(addCompanyPolicy, {
    onCompleted: () => {
      toast.success("Company Policy was successfully added");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });
  const [updateCompanyPolicyHook] = useMutation(updateCompanyPolicy, {
    onCompleted: () => {
      toast.success("Company Policy was successfully updated");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });
  const [removeCompanyPolicyHook] = useMutation(removeCompanyPolicy, {
    onCompleted: () => {
      toast.success("Company Policy was successfully Removed");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const reloadData = async () => {
    setIsLoading(true);
    await refetch();
    setIsLoading(false);
  };
  const handleView = (id: string) => {
    history.push(`/compliance/policy-hub/${id}`);
  };

  const results = useMemo(() => {
    const status = Number(filter.status.value);
    const tag = filter.tag;
    if (!filter.search) return policies.filter((item) => item.status === status && (tag ? item.tags.includes(tag.value): true) );
    const search = filter?.search?.toLowerCase();
    return policies.filter((item) =>
      item.name.toLowerCase().includes(search) && item.status === status && (tag ? item.tags.includes(tag.value): true)
    );
  }, [filter, policies]);

  const loadOptions = (inputValue: string) => new Promise<SelectOption[]>((resolve) => {
    const searchText = inputValue.toString();
    return resolve(!searchText ? tags: tags.filter((u) => u.label.toLowerCase().includes(searchText)))
  });
  
  useEffect(() => {
    if(tagResult.data) {
      const rawTags = tagResult.data.company_policy_tags as CompanyPolicyTag[];
      if(rawTags.length === 0) {
        setTags([{
          label: COMPANY_POLICY_UNCATEGORIZED_TAG,
          value: COMPANY_POLICY_UNCATEGORIZED_TAG
        }])
      } else {
        setTags(rawTags.map((u) => ({
          label: u.name,
          value: u.name
        })));
      }
    }
  },[tagResult.data]);

  useEffect(() => {
    if (data) {
      setPolicies(data.company_policies);
    }
  }, [data]);

  //#region Add Policy

  const handleOpenAddPolicy = () => {
    setInitialValuePolicy({
      name: "",
      status: CompanyPolicyStatus.ACTIVE,
      tags: []
    });
    setModal({
      name: "Add",
      action: "add",
    });
    setSelectedTags([]);
    setIsAddPolicyModalOpen(true);
  };
  const handleOpenEditPolicy = (item: CompanyPolicy) => {
    const itemTags = item.tags.map((u) => ({ label: u, value: u}));
    setInitialValuePolicy({
      name: item.name,
      status: item.status,
      policy_file_name: item?.policy_file_name,
      tags: itemTags
    });
    setSelectedTags(itemTags);
    setModal({
      name: "Update",
      action: "update",
    });
    setselectedPolicy(item);
    setIsAddPolicyModalOpen(true);
  };
  const submitAddPolicy = async (value: FormData) => {
    if (isOnProcess) return;

    setIsOnProcess(true);

    let result = null;

    if (modal.action === "add") {
      result = await addCompanyPolicyHook({
        variables: {
          record: {
            name: value.name,
            status: CompanyPolicyStatus.ACTIVE,
            policy_meta_data: value.policy_meta_data,
            policy_file_name: value.file?.name,
            tags: value.tags.map((u) => u.value)
          },
        },
      })
        .then(res => (res.errors ? false : true))
        .catch(() => false);
    } else if (modal.action === "update") {
      result = await updateCompanyPolicyHook({
        variables: {
          _id: selectedPolicy._id,
          record: {
            name: value.name,
            status: Number(value.status),
            policy_meta_data: value.policy_meta_data,
            policy_file_name: value.file?.name,
            tags: value.tags.map((u) => u.value)
          },
        },
      })
        .then(res => (res.errors ? false : true))
        .catch(() => false);
    }

    setIsOnProcess(false);
    if (!result) return;
    setIsAddPolicyModalOpen(false);
    await Promise.all([reloadData(),tagResult.refetch()]);
  };
  //#endregion

  //#region Delete Policy
  const handleOpenDeletePolicy = (item: CompanyPolicy) => {
    setselectedPolicy(item);
    setOpenDeletePolicyModal(true);
  };

  const handleDeletePolicy = async () => {
    if (isDeletingPolicy) return;

    setIsDeletingPolicy(true);
    const result = await removeCompanyPolicyHook({
      variables: {
        _id: selectedPolicy._id,
      },
    })
      .then(res => (res.errors ? false : true))
      .catch(() => false);

    setIsDeletingPolicy(false);
    if (!result) return;
    setOpenDeletePolicyModal(false);
    await reloadData();
  };
  //#endregion

  //#region History
  const handleOpenHistory = (item: CompanyPolicy) => {
    setIsHistoryModalOpen(true); 
    setSelectedPolicyHistory(item);
  }
  //#endregion

  return (
    <>
      <Breadcrumbs crumbs={breadcrumbsData} />
      {error && <Alert color="danger">Oops, we’re having trouble getting the Policy List. Please give it a minute, and if it doesn’t resolve, let us know.</Alert>}
      <Row className="mb-3">
        <Col md={4} xs={12} className="mt-1 pr-0">
            <Input 
              className="w-100" 
              type="text" 
              placeholder="Search Policy names here" 
              value={filter.search} 
              onChange={(e) => setFilter((prev) => ({...prev, search: e.target.value || ""}))} 
            />
        </Col>
        <Col md={3} xs={12} className="mt-1 h-100 pl-1 pr-0" >
          <RichFormSelect
            isClearable
            value={filter.tag}
            options={tags}
            isDisabled ={tagResult.loading}
            onChange={async (e) => {
              setFilter((prev) => ({...prev, tag: e}))
            }}
            placeholder="Filter by Tag"
          />
        </Col>
        <Col md={3} xs={12} className="mt-1 h-100 pl-1 pr-0" >
          <RichFormSelect
            value={filter.status}
            options={StatusFilterOptions}
            isSearchable={false}
            onChange={async (e) => {
              setFilter((prev) => ({...prev, status: e}))
            }}
          />
        </Col>
        <Col md={2} xs={12} className="mt-1 pl-1 ">
          <Button color="primary" onClick={handleOpenAddPolicy} className="w-100" disabled={tagResult.loading}>
            Add Policy
          </Button>
        </Col>
      </Row>

      <CompanyPolicyTable 
        data={results}
        loading={loading || isLoading}
        hasCRUD={true}
        handleView={handleView}
        handleOpenEditPolicy={handleOpenEditPolicy}
        handleOpenDeletePolicy={handleOpenDeletePolicy}
        handleOpenHistory={handleOpenHistory}
      />

      <Modal id="policy-add-modal" backdrop={true} isOpen={isAddPolicyModalOpen}>
        <ModalHeader toggle={() => setIsAddPolicyModalOpen(prev => !prev)} className="pb-0">
          <p className="text-lg">{modal.name} Policy</p>
        </ModalHeader>
        <ModalBody>
          <Formik
            innerRef={formikRef}
            initialValues={initialValuePolicy}
            validationSchema={AddCompayPolicyValidator}
            onSubmit={async (value: FormData) => {
              await submitAddPolicy(value);
            }}
          >
            {({ errors, touched, setFieldValue, values }) => (
              <Form>
                <TextFormInput name="name" placeholder="Enter Policy Name" label="Policy Name" required={true} className="mb-3"  />
                <TextCounter maxLength={MAX_TITLE_LENGTH} text={values.name} className="mb-3" />
                <FormGroup>
                  <Label>Tags *</Label>
                  <RichFormSelectCreatable
                    cacheOptions
                    isDisabled ={tagResult.loading}
                    isClearable
                    isMulti
                    defaultOptions
                    loadOptions={loadOptions}
                    isValidNewOption={(inputValue) => inputValue.trim().length >= 2}
                    noOptionsMessage={({ inputValue }) =>
                      inputValue && inputValue.trim().length <= 2
                        ? 'Type at least 2 characters to create'
                        : 'No options'
                    }
                    onChange={async (e) => {
                      setSelectedTags(e);
                      setFieldValue("tags", e);
                    }}
                    value={selectedTags}
                    placeholder="Select or create tags"
                  />
                  {
                    touched.tags && errors.tags && typeof errors.tags === 'string' &&
                    <FormError>
                      {errors.tags}
                    </FormError>
                  }
                </FormGroup>
                {
                  modal.action !== "add" && 
                  <FormGroup >
                    <Label >Status</Label>
                    <Field className="form-control" as="select" name="status" >
                      {
                        CompanyPolicyStatusOptions.map((u) => (
                          <option key={u.value} value={u.value}>{u.name}</option>
                        ))
                      }
                    </Field>
                    {
                      touched.status && errors.status &&
                      <FormError>
                        {errors.status}
                      </FormError>
                    }
                  </FormGroup> 
                }
                <FormGroup>
                  <Label>File </Label>
                  {
                    values.policy_file_name ? 
                    <Row>
                      <Col md={8} >
                        <small className="description-details">{values.policy_file_name}</small>
                      </Col>
                      <Col md={4} className="d-flex justify-content-end">
                        <small className="text-danger" onClick={() => { setFieldValue("policy_file_name", "") }} role="button">Remove</small>
                      </Col>
                    </Row>
                    :
                    <Input
                      name="file"
                      type="file"
                      accept={ValidFileFormatForPolicy}
                      className="form-control"
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        setFieldValue("file", file);
                        if (!file) {
                          setFieldValue("policy_meta_data", "");
                          return;
                        }
                        const text = await FileToText(file);
                        setFieldValue("policy_meta_data", text);
                      }}
                    />
                  }
                  {
                    touched.file && errors.file &&

                    <FormError>
                      {typeof errors.file === "string" && (
                        errors.file
                      )}
                    </FormError>
                  }
                </FormGroup>
              </Form>
            )}
          </Formik>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsAddPolicyModalOpen(false)} disabled={isOnProcess}>
            Close
          </Button>
          <Button color="primary" onClick={() => formikRef.current.submitForm()} disabled={isOnProcess}>
            {modal.name}
          </Button>
        </ModalFooter>
      </Modal>

      <PromptDialog
        isOpen={openDeletePolicyModal}
        onCancel={() => setOpenDeletePolicyModal(false)}
        onConfirm={handleDeletePolicy}
        title="Delete Policy"
        promptMessage="Action is Irreversible, all related data of this policy will be deleted."
        confirmBtnTxt="Delete"
        isOnProcess={isDeletingPolicy}
      />
      
      {selectedPolicyHistory?._id && 
      <CompanyPolicyAndNudgeHistoryModal
        isOpen={isHistoryModalOpen}
        toggle={() =>{ setIsHistoryModalOpen((prev) => !prev); setSelectedPolicyHistory(null)}}
        policyId={selectedPolicyHistory?._id}
      />}
    </>

  );
};

export { CompanyPolicyListPage as CompanyPolicyList };
